import { Link } from 'react-router-dom';
import { useState } from 'react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d6aik7ua728k8ctu3b9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (response.ok) {
        setSubmitMessage('Successfully subscribed.');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitMessage('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-stone-50 text-stone-500">
      {/* Top accent line */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-bronze/40 to-transparent" />

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-8 lg:px-14 pt-14 pb-8">

        {/* ── PRIMARY SECTION: Brand + Hours ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 pb-10">

          {/* Brand block */}
          <div className="flex flex-col gap-5">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 flex items-center justify-center shrink-0">
                  <img
                    src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/547be4984668e7b5c47ec2a331e97ec6.png"
                    alt="Vionne Logo"
                    className="w-9 h-9 object-contain"
                  />
                </div>
                <span className="font-display text-3xl font-light text-stone-800 tracking-wide italic">
                  Vionne Stability
                </span>
              </div>
              <p className="text-stone-400 text-[10px] font-sans tracking-[0.28em] uppercase mt-1.5">
                Clinical Atelier · Vancouver
              </p>
            </div>

            <p className="text-stone-500 text-[12px] font-sans font-light leading-[1.95] max-w-[320px]">
              Facial-led botanical acupuncture for nervous system stability — restoring structure, vitality, and performance.
            </p>

            {/* Credentials */}
            <p className="text-stone-400 text-[10px] font-sans tracking-[0.2em] uppercase">
              RAc. · CIDESCO · UK VTCT Level 4&nbsp;&nbsp;· IFA/CFA/NAHA
            </p>

            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-map-pin-line text-bronze text-sm"></i>
              </div>
              <p className="text-stone-400 text-[11.5px] font-sans font-light leading-relaxed">
                401-9 1388 West Broadway<br />
                Vancouver, BC V6H1H2
              </p>
            </div>

            {/* Social */}
            <div className="flex gap-2.5">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center border border-stone-300 rounded-full text-stone-400 hover:text-stone-700 hover:border-stone-500 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center border border-stone-300 rounded-full text-stone-400 hover:text-stone-700 hover:border-stone-500 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-linkedin-line text-sm"></i>
              </a>
              <a
                href="mailto:info@vionnestability.com"
                className="w-8 h-8 flex items-center justify-center border border-stone-300 rounded-full text-stone-400 hover:text-stone-700 hover:border-stone-500 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>

          {/* Hours block */}
          <div className="flex flex-col gap-4 lg:pl-10 lg:border-l border-stone-200">
            <h4 className="text-stone-600 text-[10px] font-sans font-semibold tracking-[0.28em] uppercase">
              Atelier Hours
            </h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between max-w-[260px]">
                <span className="text-stone-400 text-[12px] font-sans font-light">Monday &amp; Sunday</span>
                <span className="text-stone-300 text-[12px] font-sans font-light italic">Closed</span>
              </div>
              <div className="w-full max-w-[260px] h-px bg-stone-200/60" />
              <div className="flex items-center justify-between max-w-[260px]">
                <span className="text-stone-400 text-[12px] font-sans font-light">Tuesday &amp; Wednesday</span>
                <span className="text-stone-600 text-[12px] font-sans">12:30 – 7:30</span>
              </div>
              <div className="w-full max-w-[260px] h-px bg-stone-200/60" />
              <div className="flex items-center justify-between max-w-[260px]">
                <span className="text-stone-400 text-[12px] font-sans font-light">Thursday – Saturday</span>
                <span className="text-stone-600 text-[12px] font-sans">8:00 – 5:00</span>
              </div>
            </div>

            {/* Contact note */}
            <div className="mt-3 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-bronze text-xs"></i>
              </div>
              <a
                href="mailto:info@vionnestability.com"
                className="text-stone-400 text-[11px] font-sans font-light hover:text-stone-600 transition-colors cursor-pointer"
              >
                info@vionnestability.com
              </a>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div className="w-full h-px bg-stone-200 mb-6" />

        {/* ── SECONDARY SECTION: Nav + Newsletter (compact strip) ── */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-0 pb-6">

          {/* Services nav */}
          <div className="flex gap-8 lg:gap-10 flex-1">
            <div>
              <h5 className="text-stone-400 text-[9px] font-sans font-semibold tracking-[0.22em] uppercase mb-3">
                Services
              </h5>
              <ul className="flex flex-col gap-2">
                <li>
                  <Link to="/method" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    The Method
                  </Link>
                </li>
                <li>
                  <Link to="/clinical-services" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    Clinical
                  </Link>
                </li>
                <li>
                  <Link to="/booking" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    Booking
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-stone-400 text-[9px] font-sans font-semibold tracking-[0.22em] uppercase mb-3">
                Explore
              </h5>
              <ul className="flex flex-col gap-2">
                <li>
                  <Link to="/education" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    Programs
                  </Link>
                </li>
                <li>
                  <Link to="/product" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    Collection
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-stone-300 text-[10.5px] font-sans font-light hover:text-stone-500 transition-colors cursor-pointer">
                    About
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter — compact */}
          <div className="flex flex-col gap-2.5 lg:w-72">
            <h5 className="text-stone-400 text-[9px] font-sans font-semibold tracking-[0.22em] uppercase">
              Stay Connected
            </h5>
            <p className="text-stone-300 text-[10.5px] font-sans font-light leading-relaxed">
              Biweekly insights &amp; seasonal offerings — delivered quietly.
            </p>
            <form
              id="footer-newsletter"
              onSubmit={handleNewsletterSubmit}
              className="flex gap-2 mt-0.5"
              data-readdy-form
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 min-w-0 px-3 py-2 bg-white border border-stone-200 rounded-md text-[10.5px] font-sans text-stone-600 placeholder:text-stone-300 focus:outline-none focus:border-bronze/40 transition-colors"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-stone-200 text-stone-500 rounded-md hover:bg-bronze/80 hover:text-white transition-colors text-[9.5px] font-sans font-semibold tracking-[0.18em] uppercase whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? '...' : 'Subscribe'}
              </button>
            </form>
            {submitMessage && (
              <p className={`text-[9.5px] font-sans ${submitMessage.includes('Successfully') ? 'text-bronze' : 'text-red-400'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        {/* ── BOTTOM BAR ── */}
        <div className="w-full h-px bg-stone-200 mb-5" />
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-stone-300 text-[10px] font-sans font-light tracking-wide">
            &copy; 2026 Vionne Stability. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-stone-300 text-[10px] font-sans hover:text-stone-500 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
